import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabase";
import "./AppShell.css";

const SUPABASE_URL = "https://dpdfpskenzqtzyjoetdw.supabase.co";
const SUPABASE_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRwZGZwc2tlbnpxdHp5am9ldGR3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgxMTUxOTgsImV4cCI6MjA5MzY5MTE5OH0.-LQtmPqvj1RAY_otXztfHOYfQUVcDOEpVJjC3-GdZ6Q";
const APP_ORIGIN = window.location.origin;
function postToFrames(message) {
  document.querySelectorAll("iframe").forEach((frame) => {
    try {
      frame.contentWindow.postMessage(message, APP_ORIGIN);
    } catch (_) {}
  });
}
const DATE_FILTER_KEY = "td_date_filter";
const DEFAULT_DATE_FILTER = { period: "all", start: "", end: "" };
function readDateFilter() {
  try {
    return {
      ...DEFAULT_DATE_FILTER,
      ...JSON.parse(localStorage.getItem(DATE_FILTER_KEY) || "{}"),
    };
  } catch (_) {
    return DEFAULT_DATE_FILTER;
  }
}
function normalizeDateFilter(value) {
  const allowed = ["week", "month", "ytd", "all", "custom"];
  return {
    period: allowed.includes(value?.period) ? value.period : "all",
    start: value?.start || "",
    end: value?.end || "",
  };
}

const NAV_TABS = [
  { id: "home", label: "Home" },
  { id: "journal", label: "Journal" },
  { id: "playbook", label: "Playbook" },
  { id: "review", label: "Review" },
  { id: "learn", label: "Learn" },
];
const TAB_TOGGLES = {
  journal: [
    { id: "journal", label: "Trades" },
    { id: "calendar", label: "Calendar" },
  ],
  review: [
    { id: "insights", label: "Insights" },
    { id: "performance", label: "Performance" },
    { id: "progress", label: "Progress" },
  ],
};
const TITLES = {
  home: "Today",
  journal: "Journal",
  playbook: "Playbook",
  review: "Review",
  learn: "Learn",
  account: "Account",
  community: "Community",
  goals: "Goals",
};

function NavIcon({ id, active }) {
  const c = active ? "#1E88E5" : "currentColor";
  const common = {
    width: 23,
    height: 23,
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": true,
  };
  if (id === "home")
    return (
      <svg {...common}>
        <path
          d="M3.5 10.2 12 3.6l8.5 6.6v9.2a1 1 0 0 1-1 1h-5v-6h-5v6h-5a1 1 0 0 1-1-1v-9.2Z"
          stroke={c}
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
    );
  if (id === "journal")
    return (
      <svg {...common}>
        <rect
          x="4"
          y="3"
          width="16"
          height="18"
          rx="2.5"
          stroke={c}
          strokeWidth="1.8"
        />
        <path
          d="M8 8h8M8 12h8M8 16h5"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  if (id === "playbook")
    return (
      <svg {...common}>
        <path
          d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v17H6.5A2.5 2.5 0 0 0 4 22V5.5ZM20 5.5A2.5 2.5 0 0 0 17.5 3H13v17h4.5A2.5 2.5 0 0 1 20 22V5.5Z"
          stroke={c}
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      </svg>
    );
  if (id === "review")
    return (
      <svg {...common}>
        <path
          d="M4 19V9M10 19V5M16 19v-7M22 19V3"
          stroke={c}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path d="M3 19.5h19" stroke={c} strokeWidth="1.5" />
      </svg>
    );
  if (id === "learn")
    return (
      <svg {...common}>
        <path
          d="m3 6.5 9-4 9 4-9 4-9-4Z"
          stroke={c}
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M6 9v5.5c0 2 2.7 3.5 6 3.5s6-1.5 6-3.5V9M21 7v8"
          stroke={c}
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  return null;
}

function Toggle({ options, active, onChange }) {
  return (
    <div className="td-toggle" role="tablist">
      {options.map((opt) => (
        <button
          key={opt.id}
          role="tab"
          aria-selected={active === opt.id}
          className={active === opt.id ? "active" : ""}
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function WelcomeModal({ product, theme, onOpen, onClose }) {
  const content = {
    ctm: {
      title: "Clinical Trading Mastery is ready",
      body: "Your complete weekly-options system, implementation bonuses, and 30-day TradeDash pass are in your Library.",
      cta: "Start Clinical Trading Mastery",
    },
    profitability: {
      title: "Profitability Protocol is ready",
      body: "Your deeper guide to expected value, R-multiples, drawdowns, and winner/loser size is in your Library.",
      cta: "Open Profitability Protocol",
    },
    tos: {
      title: "Thinkorswim Setup is ready",
      body: "Your MOVE watchlist column, studies, chart setup, and implementation instructions are in your Library.",
      cta: "Set Up Thinkorswim",
    },
  }[product] || {
    title: "Your purchase is ready",
    body: "You can find it in your Library.",
    cta: "Open My Library",
  };
  return (
    <div
      className="td-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
    >
      <div className="td-welcome-card" data-theme={theme}>
        <div className="td-success-mark">✓</div>
        <div className="td-eyebrow">PURCHASE CONFIRMED</div>
        <h2 id="welcome-title">{content.title}</h2>
        <p>{content.body}</p>
        <button className="td-primary" onClick={onOpen}>
          {content.cta}
        </button>
        <button className="td-link-button" onClick={onClose}>
          Not now
        </button>
      </div>
    </div>
  );
}

export default function AppShell({ session }) {
  const [activeTab, setActiveTab] = useState("home");
  const [onboardingDone, setOnboardingDone] = useState(() =>
    localStorage.getItem("td_onboarding_done") === "1" ? true : null,
  );
  const [loadedTabs, setLoadedTabs] = useState(["home"]);
  const [welcomeProduct, setWelcomeProduct] = useState(() =>
    new URLSearchParams(window.location.search).get("purchased"),
  );
  const [theme, setTheme] = useState(
    () => localStorage.getItem("td_theme") || "light",
  );
  const [learnSrc, setLearnSrc] = useState("/library.html");
  const [subView, setSubView] = useState({
    journal: "journal",
    review: "insights",
  });
  const [showImporter, setShowImporter] = useState(false);
  const [dateFilter, setDateFilter] = useState(readDateFilter);
  const [profile, setProfile] = useState({});
  const [subStatus, setSubStatus] = useState("free");
  const [proExpiresAt, setProExpiresAt] = useState("");
  const token = session?.access_token || "";
  const uid = session?.user?.id || "";
  const toggles = TAB_TOGGLES[activeTab];
  const isReader = activeTab === "learn" && learnSrc !== "/library.html";
  const title = isReader ? "Library" : TITLES[activeTab] || "TradeDash";

  useEffect(() => {
    async function fetchStatus() {
      if (!uid) return;
      try {
        // Keep access-critical fields isolated from optional profile/product fields.
        // A missing optional column must never make a paid user look free.
        const headers = {
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${token}`,
        };
        const coreFields =
          "subscription_status,plan_selector,pro_access_expires_at,onboarding_completed";
        const coreRes = await fetch(
          `${SUPABASE_URL}/rest/v1/profiles?select=${coreFields}&id=eq.${uid}`,
          { headers },
        );
        if (!coreRes.ok) {
          setOnboardingDone(true);
          return;
        }
        const coreData = await coreRes.json();
        if (!coreData?.[0]) {
          setOnboardingDone(true);
          return;
        }
        const next = coreData[0];

        let optional = {};
        try {
          const optionalFields =
            "username,email,ctm_purchased,profitability_purchased,tos_purchased";
          const optionalRes = await fetch(
            `${SUPABASE_URL}/rest/v1/profiles?select=${optionalFields}&id=eq.${uid}`,
            { headers },
          );
          const optionalData = optionalRes.ok ? await optionalRes.json() : [];
          optional = optionalData?.[0] || {};
        } catch (_) {}
        setProfile({ ...next, ...optional });
        const localDone = localStorage.getItem("td_onboarding_done") === "1";
        if (next.onboarding_completed === true || localDone) {
          setOnboardingDone(true);
          localStorage.setItem("td_onboarding_done", "1");
          if (localDone && next.onboarding_completed !== true)
            fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${uid}`, {
              method: "PATCH",
              headers: {
                apikey: SUPABASE_KEY,
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                Prefer: "return=minimal",
              },
              body: JSON.stringify({ onboarding_completed: true }),
            }).catch(() => {});
        } else setOnboardingDone(false);
        const expired =
          next.plan_selector === "one_time" &&
          next.pro_access_expires_at &&
          new Date(next.pro_access_expires_at) <= new Date();
        setSubStatus(expired ? "free" : next.subscription_status || "free");
        setProExpiresAt(
          next.plan_selector === "one_time" && !expired
            ? next.pro_access_expires_at || ""
            : "",
        );
      } catch (_) {
        setOnboardingDone(true);
      }
    }
    fetchStatus();
  }, [uid, token]);

  const authPayload = useMemo(
    () => ({
      type: "AUTH",
      token,
      userId: uid,
      plan: subStatus,
      expires: proExpiresAt,
      dateFilter,
      entitlements: {
        ctm: !!profile.ctm_purchased,
        profitability: !!profile.profitability_purchased,
        tos: !!profile.tos_purchased,
      },
      profile: {
        username: profile.username || "",
        email: profile.email || "",
        subscriptionStatus: profile.subscription_status || subStatus,
        planSelector: profile.plan_selector || "",
        proAccessExpiresAt: profile.pro_access_expires_at || proExpiresAt,
      },
    }),
    [token, uid, subStatus, proExpiresAt, profile, dateFilter],
  );
  function framePayload() {
    return authPayload;
  }
  useEffect(() => {
    const timer = setTimeout(() => postToFrames(authPayload), 60);
    return () => clearTimeout(timer);
  }, [authPayload]);
  useEffect(() => {
    postToFrames({ type: "PLAN", plan: subStatus, expires: proExpiresAt });
  }, [subStatus, proExpiresAt]);
  useEffect(() => {
    document.body.setAttribute("data-theme", theme === "light" ? "light" : "");
    localStorage.setItem("td_theme", theme);
    const timer = setTimeout(() => postToFrames({ type: "THEME", theme }), 100);
    return () => clearTimeout(timer);
  }, [theme]);
  useEffect(() => {
    localStorage.setItem(DATE_FILTER_KEY, JSON.stringify(dateFilter));
    postToFrames({ type: "DATE_FILTER", filter: dateFilter });
  }, [dateFilter]);

  function openTab(next, view) {
    if (next === "performance") next = "review";
    if (next === "leaderboard") next = "community";
    if (next === "progress") {
      setSubView((prev) => ({ ...prev, review: "progress" }));
      next = "review";
    }
    if (view) setSubView((prev) => ({ ...prev, [next]: view }));
    setActiveTab(next);
    setLoadedTabs((prev) => (prev.includes(next) ? prev : [...prev, next]));
    if (next === "home")
      setTimeout(() => postToFrames({ type: "ACTIVATION_REFRESH" }), 80);
  }
  function openLearn(key = "library") {
    const pages = {
      library: "library",
      course: "learn-course",
      coursev2: "learn-course-v2",
      tools: "learn-tools",
      challenge: "learn-challenge",
      quickstart: "learn-quick-start",
      move: "learn-move",
      management: "learn-management",
      riskcalc: "learn-risk-calculator",
      solomon: "learn-solomon",
      tos: "learn-tos",
      profitability: "learn-profitability",
      guide: "learn-guide",
      roadmap: "learn-roadmap",
      bonuses: "learn-bonuses",
      admin: "admin",
    };
    if ((key === "course" || key === "coursev2") && uid)
      localStorage.setItem(`td_ctm_started_${uid}`, "1");
    setLearnSrc(`/${pages[key] || "library"}.html`);
    openTab("learn");
  }

  useEffect(() => {
    function handleMessage(e) {
      if (e.origin !== APP_ORIGIN || !e.data) return;
      if (e.data.type === "OPEN_HOME_VIEW")
        return e.data.view === "progress"
          ? openTab("review", "progress")
          : openTab("home");
      if (e.data.type === "OPEN_TAB") return openTab(e.data.tab);
      if (e.data.type === "OPEN_IMPORT") return setShowImporter(true);
      if (e.data.type === "OPEN_LEARN") return openLearn(e.data.tab);
      if (e.data.type === "THEME_REQUEST") {
        setTheme(e.data.theme === "light" ? "light" : "dark");
        return;
      }
      if (e.data.type === "DATE_FILTER_CHANGE") {
        setDateFilter(normalizeDateFilter(e.data.filter));
        return;
      }
      if (e.data.type === "ONBOARDING_COMPLETE") {
        if (welcomeProduct) closeWelcome();
        setOnboardingDone(true);
        localStorage.setItem("td_onboarding_done", "1");
        if (e.data.destination === "learn")
          openLearn(e.data.learnTab || "library");
        else if (e.data.destination === "playbook") openTab("playbook");
        else if (e.data.skipToImporter) setShowImporter(true);
        else openTab("home");
      }
      if (e.data.type === "SIGNOUT") supabase.auth.signOut();
      if (e.data.type === "IMPORT_DONE") {
        setShowImporter(false);
        openTab("journal");
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  });

  const allTabs = useMemo(
    () => [
      ...NAV_TABS,
      { id: "account" },
      { id: "goals" },
      { id: "community" },
    ],
    [],
  );
  function withPlan(path) {
    const params = new URLSearchParams();
    params.set("plan", subStatus);
    if (proExpiresAt) params.set("expires", proExpiresAt);
    return `${path}?${params.toString()}`;
  }
  function getTabSrc(tabId) {
    if (tabId === "home") return withPlan("/home.html");
    if (tabId === "journal")
      return withPlan(
        subView.journal === "calendar" ? "/calendar.html" : "/journal.html",
      );
    if (tabId === "playbook") return withPlan("/playbook.html");
    if (tabId === "review")
      return withPlan(
        subView.review === "insights"
          ? "/insights.html"
          : subView.review === "progress"
            ? "/progress.html"
            : "/analysis.html",
      );
    if (tabId === "learn") return withPlan(learnSrc);
    if (tabId === "account") return withPlan("/account.html");
    if (tabId === "goals") return withPlan("/goals.html");
    if (tabId === "community") return withPlan("/leaderboard.html");
    return null;
  }
  function closeWelcome() {
    setWelcomeProduct(null);
    const clean = new URL(window.location.href);
    clean.searchParams.delete("purchased");
    window.history.replaceState({}, "", clean.pathname + clean.search);
  }
  function openPurchasedContent() {
    const product = welcomeProduct;
    closeWelcome();
    if (product === "ctm") openLearn("course");
    else if (product === "profitability") openLearn("profitability");
    else if (product === "tos") openLearn("tos");
    else openLearn("library");
  }

  return (
    <div className="td-shell" data-theme={theme}>
      {onboardingDone === null && (
        <div className="td-boot">
          <img src="/tradedash-logo.svg" alt="TradeDash" />
          <span>Preparing your workspace…</span>
        </div>
      )}
      {onboardingDone === false && (
        <div className="td-onboarding-gate">
          <iframe
            title="TradeDash Onboarding"
            src={withPlan("/onboarding.html")}
            onLoad={(e) => {
              e.target.contentWindow.postMessage(
                { type: "THEME", theme },
                APP_ORIGIN,
              );
              e.target.contentWindow.postMessage(framePayload(), APP_ORIGIN);
            }}
          />
        </div>
      )}
      {welcomeProduct && onboardingDone === true && (
        <WelcomeModal
          product={welcomeProduct}
          theme={theme}
          onOpen={openPurchasedContent}
          onClose={closeWelcome}
        />
      )}
      <header className="td-header">
        <div className="td-header-main">
          <div className="td-header-left">
            {isReader ? (
              <button
                className="td-icon-button"
                aria-label="Back to Library"
                onClick={() => openLearn("library")}
              >
                ‹
              </button>
            ) : (
              <button
                className="td-import-button"
                aria-label="Import trades"
                onClick={() => setShowImporter(true)}
              >
                <span>＋</span>
                <b>Import</b>
              </button>
            )}
          </div>
          <button
            className="td-header-logo"
            aria-label="TradeDash Home"
            onClick={() => openTab("home")}
          >
            <img src="/tradedash-logo.svg" alt="TradeDash" />
          </button>
          <span className="td-screen-reader-title">{title}</span>
          <div className="td-header-actions">
            <button
              className="td-avatar"
              aria-label="Open account"
              onClick={() => openTab("account")}
            >
              {(profile.username ||
                session?.user?.email ||
                "M")[0].toUpperCase()}
            </button>
          </div>
        </div>
        {toggles && (
          <Toggle
            options={toggles}
            active={subView[activeTab]}
            onChange={(v) =>
              setSubView((prev) => ({ ...prev, [activeTab]: v }))
            }
          />
        )}
      </header>
      <main className={`td-frame-area ${toggles ? "has-toggle" : ""}`}>
        {allTabs.map((tab) => {
          const src = getTabSrc(tab.id);
          if (!src || !loadedTabs.includes(tab.id)) return null;
          const key = `${tab.id}-${tab.id === "journal" ? subView.journal : tab.id === "review" ? subView.review : tab.id === "learn" ? learnSrc : ""}`;
          return (
            <iframe
              key={key}
              src={src}
              title={tab.id}
              className={activeTab === tab.id ? "active" : ""}
              onLoad={(e) => {
                e.target.contentWindow.postMessage(
                  { type: "THEME", theme },
                  APP_ORIGIN,
                );
                e.target.contentWindow.postMessage(framePayload(), APP_ORIGIN);
              }}
            />
          );
        })}
      </main>
      <nav className="td-bottom-nav" aria-label="Primary navigation">
        {NAV_TABS.map((tab) => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? "active" : ""}
            onClick={() =>
              tab.id === "learn" ? openLearn("library") : openTab(tab.id)
            }
            aria-current={activeTab === tab.id ? "page" : undefined}
          >
            <NavIcon id={tab.id} active={activeTab === tab.id} />
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>
      {showImporter && (
        <div className="td-import-modal">
          <div className="td-import-header">
            <div>
              <strong>Import trades</strong>
              <span>Add fills, then grade Entry · Risk · Exit</span>
            </div>
            <button onClick={() => setShowImporter(false)}>Done</button>
          </div>
          <iframe
            title="TradeDash Importer"
            src={withPlan("/importer.html")}
            onLoad={(e) => {
              e.target.contentWindow.postMessage(
                { type: "THEME", theme },
                APP_ORIGIN,
              );
              e.target.contentWindow.postMessage(framePayload(), APP_ORIGIN);
            }}
          />
        </div>
      )}
    </div>
  );
}
